/**
 * WEIGHTED STRAIGHT SKELETON — the roof face solver's wavefront core (2026-08-10
 * round, docs/MODELER.md "ROOF STRAIGHT-SKELETON SOLVER"). Replaces the
 * lower-envelope face solve for sloped roofs so non-convex footprints (L/T/plus)
 * resolve with TRUE VALLEYS — the envelope is a concave-only surface, so a valley
 * can never materialize from it (the recorded ROOF-round caveat this retires).
 *
 * Pure plan-space kinetic simulation over the BEARING ring. Each non-gable edge's
 * front line moves inward at speed 1/m_i per unit of z (steeper = slower); gable
 * edges are STATIC (speed 0 — the standard vertical-face treatment: neighbor
 * vertices slide along them, the gable itself sweeps no face). A wavefront vertex
 * rides the intersection of its two adjacent front lines; three event kinds drive
 * the run:
 *   - EDGE events (a front segment collapses) merge two vertices;
 *   - SPLIT events (a reflex vertex hits an opposing front) divide the wavefront —
 *     valleys are the traces of reflex vertices;
 *   - PINCHES (antiparallel fronts meeting — same-line ridges) can't be expressed
 *     as a front-intersection velocity, so they resolve INSTANTLY: sweep the
 *     coincidence line to the nearer neighbor, emit the ridge arc, repeat.
 * Arc segments traced by vertices between birth and death ARE the skeleton; each
 * face polygon chains its wall edge + its arcs (face `l` is on the LEFT of a→b).
 *
 * For CONVEX footprints the weighted skeleton is exactly the lower envelope (the
 * z-level sets are intersections of half-planes), so existing roofs solve
 * identically. Small-n footprints only — correctness and robustness over
 * asymptotics (events recomputed from scratch after every mutation), and every
 * degenerate path fails SOFT (returns null) so the caller can fall back to the
 * lower-envelope solve.
 */
import type { Pt2 } from '@aj/bim-kernel'

const T_EPS = 1e-9
const ANG_EPS = 1e-9
const MERGE_TOL = 1e-7
const HUGE_VEL = 1e8
const ARC_MIN = 1e-9
/**
 * sin(angle) below which a SAME-FACING static/moving front pair reads as
 * parallel (2026-08-12, the defines-slope tear round): far above real drawing
 * drift (Matt's live footprint carries ~5e-6 rad between "parallel" edges),
 * far below any intentional corner.
 */
const OVERRUN_SIN = 1e-4

interface WV {
  pos0: Pt2
  t0: number
  vel: Pt2
  prevEdge: number
  nextEdge: number
  prev: WV
  next: WV
  dead: boolean
}

interface Arc {
  /** original edge index whose face is on the LEFT of a→b */
  l: number
  /** original edge index whose face is on the RIGHT of a→b */
  r: number
  a: Pt2
  b: Pt2
}

class SkelFail extends Error {}
const fail = (msg: string): never => { throw new SkelFail(msg) }

const keyOf = (p: Pt2) => `${Math.round(p[0] * 1e6)},${Math.round(p[1] * 1e6)}`

/**
 * Face polygons of the weighted straight skeleton of `ring` (CCW, no duplicate
 * closing point). `speeds[i]` is edge i's inward front speed (plan feet per unit
 * time); 0 marks a STATIC (gable) edge that sweeps no face. Returns one ring per
 * edge (null for static/empty), each starting [ring[i], ring[i+1], ...arcs...] in
 * CCW order — or null when the input is degenerate or the simulation could not
 * complete robustly (caller falls back to the envelope solve).
 */
export function computeSkeletonFaceRings(ring: Pt2[], speeds: number[]): (Pt2[] | null)[] | null {
  try {
    return run(ring, speeds)
  } catch (e) {
    if (e instanceof SkelFail) {
      if ((globalThis as { __SKEL_DEBUG?: boolean }).__SKEL_DEBUG) console.log('[skel fail]', e.message)
      return null
    }
    throw e
  }
}

function run(ring: Pt2[], speeds: number[]): (Pt2[] | null)[] {
  const n = ring.length
  if (n < 3 || speeds.length !== n) fail('bad input')
  if (!speeds.some((s) => s > 0)) fail('no moving fronts')

  // fixed per-edge geometry — front lines are analytic: a point of edge i's front
  // at time t is ring[i] + nrm[i]·(speeds[i]·t), direction dir[i] (CCW travel)
  const dir: Pt2[] = []
  const nrm: Pt2[] = []
  for (let i = 0; i < n; i++) {
    const a = ring[i], b = ring[(i + 1) % n]
    const dx = b[0] - a[0], dy = b[1] - a[1]
    const len = Math.hypot(dx, dy)
    if (len < 1e-9) fail('zero-length edge')
    dir.push([dx / len, dy / len])
    nrm.push([-dy / len, dx / len])
  }

  const arcs: Arc[] = []
  const all: WV[] = []

  const posAt = (v: WV, t: number): Pt2 => [
    v.pos0[0] + v.vel[0] * (t - v.t0),
    v.pos0[1] + v.vel[1] * (t - v.t0),
  ]
  const dist = (p: Pt2, q: Pt2) => Math.hypot(p[0] - q[0], p[1] - q[1])

  /** kill v, recording its traced arc (face prevEdge on the LEFT of birth→death) */
  function emitTrace(v: WV, t: number, at?: Pt2): void {
    const end = at ?? posAt(v, t)
    v.dead = true
    if (dist(v.pos0, end) > ARC_MIN) arcs.push({ l: v.prevEdge, r: v.nextEdge, a: v.pos0, b: end })
  }

  /** arc between two fronts along a swept/leftover segment — side resolved from eL's normal */
  function emitJoinArc(eL: number, eR: number, a: Pt2, b: Pt2): void {
    if (dist(a, b) <= ARC_MIN) return
    const d: Pt2 = [b[0] - a[0], b[1] - a[1]]
    const len = Math.hypot(d[0], d[1])
    const ln: Pt2 = [-d[1] / len, d[0] / len]
    // face eL lies BEHIND its front (the −nrm side)
    const leftIsL = ln[0] * -nrm[eL][0] + ln[1] * -nrm[eL][1] > 0
    arcs.push(leftIsL ? { l: eL, r: eR, a, b } : { l: eR, r: eL, a, b })
  }

  /** vertex velocity riding fronts eL/eR; 'pinch' when they are antiparallel,
   *  'overrun' when they are SAME-FACING parallel with one static (gable) side */
  function mkVel(eL: number, eR: number): Pt2 | 'pinch' | 'overrun' {
    const nL = nrm[eL], nR = nrm[eR]
    const sL = speeds[eL], sR = speeds[eR]
    const det = nL[0] * nR[1] - nL[1] * nR[0]
    // SAME-FACING near-parallel static/moving pair (2026-08-12, the defines-slope
    // tear round): the moving front has reached a parallel gable line at one end
    // of its segment. Before this, an EXACT parallel failed the whole solve and a
    // drawing-tolerance near-parallel silently rode the generic branch — a
    // velocity of ~1/sin(drift) racing in whichever direction the sub-arcsecond
    // drift happened to point, which could trace a PHANTOM full-width boundary
    // through the interior (Matt's collapsed main gable: the south front died at
    // the SW jog's infinite line). The correct roof semantic is Matt's tear rule
    // seen from the other side: a tear forms only along a TRANSVERSAL no-slope
    // rail; a PARALLEL no-slope edge is simply OVERRUN — the moving front
    // consumes its segment and follows through. Resolved instantly, like a pinch.
    if (nL[0] * nR[0] + nL[1] * nR[1] > 0 && Math.abs(det) <= OVERRUN_SIN &&
        (sL === 0) !== (sR === 0)) return 'overrun'
    // NEAR-ANTIPARALLEL static/moving pair (2026-08-12 r3, the pWCNl runaway
    // line): a front dying at a drawing-tolerance-off-parallel gable line used
    // to ride the generic branch — a ~1/sin(drift) velocity racing hundreds of
    // feet along the line before an event stopped it, leaving a zero-width fold
    // the area audit can't see (it reached output once the tear path stopped
    // falling back to the envelope). Route it to the PINCH resolution instead:
    // exactly-drawn lines pinch correctly; drifted ones fail resolvePinch's
    // collinearity sanity check and the whole solve falls soft to the envelope
    // — no phantom geometry either way.
    if (nL[0] * nR[0] + nL[1] * nR[1] < 0 && Math.abs(det) <= OVERRUN_SIN &&
        (sL === 0) !== (sR === 0)) return 'pinch'
    // NEAR-ANTIPARALLEL MOVING pair (2026-10-13, garage roof zTmgh6I5Esr9): two sloped fronts
    // facing each other across a drawing-drift angle (det 1.5e-8 there — two eaves drawn
    // "parallel" by hand) meet as a RIDGE. The generic branch below divided by that det and
    // failed the whole solve ('velocity blow-up'), dropping the roof to the envelope, where
    // one eave's plane swept across three others and dove below the floor. Same treatment
    // as the static/moving pair above: resolve it as a pinch.
    if (nL[0] * nR[0] + nL[1] * nR[1] < 0 && Math.abs(det) <= OVERRUN_SIN &&
        sL > 0 && sR > 0) return 'pinch'
    if (Math.abs(det) > ANG_EPS) {
      const v: Pt2 = [(sL * nR[1] - sR * nL[1]) / det, (sR * nL[0] - sL * nR[0]) / det]
      if (Math.hypot(v[0], v[1]) > HUGE_VEL) fail('velocity blow-up')
      return v
    }
    if (nL[0] * nR[0] + nL[1] * nR[1] > 0) {
      // collinear continuation — only well-defined at equal speeds (a slope break
      // along one wall line is a discontinuous surface: envelope fallback)
      if (Math.abs(sL - sR) > 1e-9) fail('collinear speed break')
      return [nL[0] * sL, nL[1] * sL]
    }
    return 'pinch'
  }

  function insertVertex(pos: Pt2, t: number, eL: number, eR: number, prev: WV, next: WV): void {
    const vel = mkVel(eL, eR)
    const V: WV = {
      pos0: [pos[0], pos[1]], t0: t,
      vel: vel === 'pinch' || vel === 'overrun' ? [0, 0] : vel,
      prevEdge: eL, nextEdge: eR,
      prev, next, dead: false,
    }
    prev.next = V
    next.prev = V
    all.push(V)
    if (vel === 'pinch') resolvePinch(V, t)
    else if (vel === 'overrun') resolveOverrun(V, t)
  }

  /**
   * M sits between SAME-FACING parallel fronts — one static (a gable line), one
   * moving (defines-slope) — the moving front having just arrived at the static
   * line at one end of the static edge's wavefront segment. A gable edge holds
   * no face and cannot outlast a front that has reached its own line: the front
   * OVERRUNS it instantly (the pinch sweep's same-facing sibling), emitting the
   * moving face's boundary along the consumed static segment, then re-seating
   * past its far end — the follow-through. The static line does NOT extend
   * through the interior; tears form only along transversal no-slope rails.
   */
  function resolveOverrun(M: WV, t: number): void {
    const mPos = posAt(M, t)
    const P = M.prev, N0 = M.next
    if (P === M || N0 === M) { M.dead = true; return }
    const staticOnLeft = speeds[M.prevEdge] === 0
    const eStatic = staticOnLeft ? M.prevEdge : M.nextEdge
    const eMoving = staticOnLeft ? M.nextEdge : M.prevEdge
    const N = staticOnLeft ? P : N0
    if (P === N0) {
      // 2-loop: the leftover static stretch is the final boundary
      emitJoinArc(eStatic, eMoving, mPos, posAt(N, t))
      emitTrace(N, t)
      M.dead = true
      return
    }
    const nPos = posAt(N, t)
    emitJoinArc(eStatic, eMoving, mPos, nPos)
    emitTrace(N, t, nPos)
    M.dead = true
    if (staticOnLeft) insertVertex(nPos, t, N.prevEdge, eMoving, N.prev, M.next)
    else insertVertex(nPos, t, eMoving, N.nextEdge, M.prev, N.next)
  }

  /**
   * M sits between antiparallel coincident fronts (a forming ridge). The ridge
   * sweeps the coincidence line instantly: consume the nearer neighbor, emit the
   * ridge arc, and re-seat the contact vertex — cascading until the local
   * configuration is generic again.
   */
  function resolvePinch(M: WV, t: number): void {
    const mPos = posAt(M, t)
    const P = M.prev, N = M.next
    if (P === M || N === M) { M.dead = true; return }
    if (P === N) {
      // 2-loop: the leftover segment between the two fronts is the final ridge
      const end = posAt(P, t)
      emitJoinArc(M.prevEdge, M.nextEdge, mPos, end)
      emitTrace(P, t)
      M.dead = true
      return
    }
    const pPos = posAt(P, t), nPos = posAt(N, t)
    const dP = dist(mPos, pPos), dN = dist(mPos, nPos)
    if (dP <= MERGE_TOL) {
      // zero-length front on the P side — plain merge, then re-check
      emitTrace(P, t, mPos)
      M.dead = true
      insertVertex(mPos, t, P.prevEdge, M.nextEdge, P.prev, N)
      return
    }
    if (dN <= MERGE_TOL) {
      emitTrace(N, t, mPos)
      M.dead = true
      insertVertex(mPos, t, M.prevEdge, N.nextEdge, P, N.next)
      return
    }
    // both neighbors sit on the coincidence line; sanity-check collinearity.
    // Tolerance is DRIFT-SCALED (r3): near-antiparallel pairs (mkVel routes
    // anything within OVERRUN_SIN here) put neighbor positions off the ideal
    // line by up to drift·span — still a real pinch, resolved deterministically
    // (the sweep is bounded by the neighbors, so it can never race past the
    // static segment the way the generic velocity did). Garbage topology still
    // fails: its offsets are feet, not thousandths.
    const u: Pt2 = [(pPos[0] - mPos[0]) / dP, (pPos[1] - mPos[1]) / dP]
    const cN = (nPos[0] - mPos[0]) * u[1] - (nPos[1] - mPos[1]) * u[0]
    const pN = (nPos[0] - mPos[0]) * u[0] + (nPos[1] - mPos[1]) * u[1]
    const driftTol = 1e-5 + (dP + Math.abs(pN)) * OVERRUN_SIN * 2
    if (Math.abs(cN) > driftTol || pN < -driftTol) fail('pinch geometry')
    if (Math.abs(dP - pN) <= MERGE_TOL * 10) {
      // both fronts end together — consume both neighbors
      emitJoinArc(M.prevEdge, M.nextEdge, mPos, pPos)
      emitTrace(P, t, pPos)
      emitTrace(N, t, pPos)
      M.dead = true
      if (P.prev === N) return // 3-loop fully consumed
      insertVertex(pPos, t, P.prevEdge, N.nextEdge, P.prev, N.next)
      return
    }
    if (dP < pN) {
      emitJoinArc(M.prevEdge, M.nextEdge, mPos, pPos)
      emitTrace(P, t, pPos)
      M.dead = true
      insertVertex(pPos, t, P.prevEdge, M.nextEdge, P.prev, N)
    } else {
      const end: Pt2 = [mPos[0] + u[0] * pN, mPos[1] + u[1] * pN]
      emitJoinArc(M.prevEdge, M.nextEdge, mPos, end)
      emitTrace(N, t, end)
      M.dead = true
      insertVertex(end, t, M.prevEdge, N.nextEdge, P, N.next)
    }
  }

  function processEdgeEvent(A: WV, B: WV, t: number): void {
    const pa = posAt(A, t), pb = posAt(B, t)
    const pos: Pt2 = [(pa[0] + pb[0]) / 2, (pa[1] + pb[1]) / 2]
    emitTrace(A, t, pos)
    emitTrace(B, t, pos)
    if (A.prev === B) return // 2-loop gone
    const prev = A.prev, next = B.next
    if (prev === next) {
      // triangle collapse — in exact math all three meet at a point; emit the
      // (usually zero-length) leftover between the two surviving fronts
      const third = posAt(prev, t)
      emitTrace(prev, t)
      emitJoinArc(A.prevEdge, B.nextEdge, pos, third)
      return
    }
    insertVertex(pos, t, A.prevEdge, B.nextEdge, prev, next)
  }

  function processSplitEvent(V: WV, C: WV, D: WV, t: number): void {
    const P = posAt(V, t)
    const k = C.nextEdge
    emitTrace(V, t, P)
    const U = V.prev, W = V.next
    // loop 1: U →(eL)→ V1 →(k)→ D…   loop 2: C →(k)→ V2 →(eR)→ W…
    insertVertex(P, t, V.prevEdge, k, U, D)
    insertVertex(P, t, k, V.nextEdge, C, W)
  }

  function finishSmallLoop(members: WV[], t: number): void {
    if (members.length === 2) {
      const [X, Y] = members
      const px = posAt(X, t), py = posAt(Y, t)
      emitTrace(X, t)
      emitTrace(Y, t)
      emitJoinArc(X.nextEdge, Y.nextEdge, px, py)
    } else {
      for (const v of members) emitTrace(v, t)
    }
  }

  // seed vertices from the wall ring at t = 0
  const seed: WV[] = ring.map((p, i) => ({
    pos0: [p[0], p[1]] as Pt2, t0: 0, vel: [0, 0] as Pt2,
    prevEdge: (i + n - 1) % n, nextEdge: i,
    prev: null as unknown as WV, next: null as unknown as WV, dead: false,
  }))
  for (let i = 0; i < n; i++) {
    seed[i].prev = seed[(i + n - 1) % n]
    seed[i].next = seed[(i + 1) % n]
    const vel = mkVel(seed[i].prevEdge, seed[i].nextEdge)
    if (vel === 'pinch') throw new SkelFail('degenerate spike corner')
    // a seed overrun (a bearing edge continuing collinearly as a gable on the
    // same line) is untested territory — fail soft to the envelope, which is
    // exactly what this configuration got before overruns existed
    if (vel === 'overrun') throw new SkelFail('seed overrun')
    seed[i].vel = vel
    all.push(seed[i])
  }

  let t = 0
  const CAP = 64 + 8 * n * n
  for (let iter = 0; iter < CAP; iter++) {
    // gather live loops (finishing degenerate ones as found)
    const visited = new Set<WV>()
    const loops: WV[][] = []
    let anyLive = false
    for (const v of all) {
      if (v.dead || visited.has(v)) continue
      const members: WV[] = []
      let cur = v
      let guard = all.length + 1
      do {
        if (cur.dead) { members.length = 0; break }
        visited.add(cur)
        members.push(cur)
        cur = cur.next
        if (--guard < 0) fail('corrupt loop')
      } while (cur !== v)
      if (!members.length) continue
      if (members.length < 3) { finishSmallLoop(members, t); continue }
      loops.push(members)
      anyLive = true
    }
    if (!anyLive) break

    // candidate events, recomputed from scratch (small n — robustness first)
    const sel: { ev: { tau: number; kind: 0 | 1; a: WV; b: WV; c?: WV } | null } = { ev: null }
    const consider = (tau: number, kind: 0 | 1, a: WV, b: WV, c?: WV) => {
      if (tau < t - T_EPS) return
      const tt = Math.max(tau, t)
      const cur = sel.ev
      if (!cur || tt < cur.tau - T_EPS || (Math.abs(tt - cur.tau) <= T_EPS && kind < cur.kind)) {
        sel.ev = { tau: tt, kind, a, b, c }
      }
    }
    for (const loop of loops) {
      for (const A of loop) {
        const B = A.next
        const e = A.nextEdge
        // edge collapse: separation along the (shared) front line direction
        const d = dir[e]
        const rel: Pt2 = [
          (B.pos0[0] - B.vel[0] * B.t0) - (A.pos0[0] - A.vel[0] * A.t0),
          (B.pos0[1] - B.vel[1] * B.t0) - (A.pos0[1] - A.vel[1] * A.t0),
        ]
        const c0 = rel[0] * d[0] + rel[1] * d[1]
        const c1 = (B.vel[0] - A.vel[0]) * d[0] + (B.vel[1] - A.vel[1]) * d[1]
        const sepNow = c0 + c1 * t
        if (sepNow <= MERGE_TOL) consider(t, 0, A, B)
        else if (c1 < -T_EPS) consider(-c0 / c1, 0, A, B)
      }
      for (const V of loop) {
        // reflex test — front directions are constant, so this is time-invariant
        const cr = dir[V.prevEdge][0] * dir[V.nextEdge][1] - dir[V.prevEdge][1] * dir[V.nextEdge][0]
        if (cr >= -ANG_EPS) continue
        for (const C of loop) {
          const D = C.next
          if (C === V || D === V) continue
          const k = C.nextEdge
          const nk = nrm[k]
          const den = V.vel[0] * nk[0] + V.vel[1] * nk[1] - speeds[k]
          if (den >= -T_EPS) continue
          const num =
            (V.vel[0] * nk[0] + V.vel[1] * nk[1]) * V.t0 -
            ((V.pos0[0] - ring[k][0]) * nk[0] + (V.pos0[1] - ring[k][1]) * nk[1])
          const tau = num / den
          if (tau < t - T_EPS) continue
          // containment: hit must land within the front segment at tau
          const hit = posAt(V, tau)
          const pc = posAt(C, tau), pd = posAt(D, tau)
          const sC = (hit[0] - pc[0]) * dir[k][0] + (hit[1] - pc[1]) * dir[k][1]
          const sD = (pd[0] - hit[0]) * dir[k][0] + (pd[1] - hit[1]) * dir[k][1]
          if (sC < -1e-6 || sD < -1e-6) continue
          consider(tau, 1, V, C, D)
        }
      }
    }
    const ev = sel.ev
    if (!ev) throw new SkelFail('wavefront stuck')
    t = ev.tau
    if (ev.kind === 0) processEdgeEvent(ev.a, ev.b, t)
    else processSplitEvent(ev.a, ev.b, ev.c as WV, t)
  }
  if (all.some((v) => !v.dead)) fail('iteration cap')

  return chainFaces(ring, speeds, arcs)
}

/** assemble each moving edge's face polygon: wall edge + directed arc chain */
function chainFaces(ring: Pt2[], speeds: number[], arcs: Arc[]): (Pt2[] | null)[] {
  const n = ring.length
  const out: (Pt2[] | null)[] = []
  for (let f = 0; f < n; f++) {
    if (!(speeds[f] > 0)) { out.push(null); continue }
    const nextFrom = new Map<string, Pt2>()
    for (const arc of arcs) {
      // an arc below the key resolution (keyOf(a) === keyOf(b)) is a SELF-LOOP:
      // it can never advance a chain walk — only alias a real arc's start key
      // (a duplicate-key fail) or spin the walk in place (a loop-guard fail).
      // The offset-parallel-bearing-eaves pinch sweep leaves exactly such
      // slivers (2026-08-19, the HIOM T-roof: same-facing south eaves 0.58 ft
      // apart left a zero-length ridge leftover that killed the whole solve to
      // the envelope — gables gone, planes sweeping 6 ft below the plate).
      if (keyOf(arc.a) === keyOf(arc.b)) continue
      if (arc.l === f) {
        const k = keyOf(arc.a)
        if (nextFrom.has(k)) fail('ambiguous face boundary')
        nextFrom.set(k, arc.b)
      } else if (arc.r === f) {
        const k = keyOf(arc.b)
        if (nextFrom.has(k)) fail('ambiguous face boundary')
        nextFrom.set(k, arc.a)
      }
    }
    const a = ring[f], b = ring[(f + 1) % n]
    const pts: Pt2[] = [[a[0], a[1]], [b[0], b[1]]]
    const aKey = keyOf(a)
    let cur = b
    let guard = arcs.length + 4
    while (keyOf(cur) !== aKey) {
      const nxt = nextFrom.get(keyOf(cur))
      if (!nxt) fail('open face boundary')
      pts.push([nxt![0], nxt![1]])
      cur = nxt!
      if (--guard < 0) fail('face boundary loop')
    }
    pts.pop() // the walk re-reaches `a`, already pts[0]
    out.push(pts)
  }
  return out
}
